<script lang="ts" setup>
import FolderListGroup from '@/components/sidebar/FolderListGroup.vue';
import type {
  FolderListItemComputedState,
  IFolderListGroupProps,
  UnseenMailsCount
} from '@/types/components/folder/FolderListGroup';
import useFolderListProvides from '@/composables/useFolderListProvides';
import { useRoute } from 'vue-router';
import type { IHashedFolderItem } from '@/types/services/folder.service';
import { ROUTE_NAMES } from '@/router/conf';
import { computed } from 'vue';
import {
  EFolderOptionalSpecialUse,
  EFolderSystemSpecialUse,
  type EFolderSpecialUse
} from '@/types/services/folder.service.enums';
import useMailboxListDrag from '@/composables/mailbox/useMailboxListDrag';
import type { RouteLocationRaw } from 'vue-router';

const props = defineProps<IFolderListGroupProps>();
const route = useRoute();

const { injectFolderListOpened, injectFolderListDraggedOver } = useFolderListProvides();
const { openedFolders, toggleFolder } = injectFolderListOpened();
const { draggedOverFolder, setDraggedOverFolder } = injectFolderListDraggedOver();
const { handleDrop } = useMailboxListDrag();

const folderMap: Record<EFolderSpecialUse, string> = {
  [EFolderSystemSpecialUse.DRAFTS]: 'file-outline',
  [EFolderSystemSpecialUse.SENT]: 'send-outline',
  [EFolderSystemSpecialUse.TRASH]: 'trash-can-outline',
  [EFolderSystemSpecialUse.JUNK]: 'alert-octagon-outline',
  [EFolderOptionalSpecialUse.ARCHIVE]: 'archive-arrow-down-outline',
  [EFolderSystemSpecialUse.INBOX]: 'inbox-outline'
};
const computeForItem = (item: IHashedFolderItem): FolderListItemComputedState => {
  // icon
  const systemFolderIcon = (() => {
    if (item.specialUse && item?.specialUse in folderMap) {
      return folderMap[item.specialUse as EFolderSpecialUse];
    }
    if (item.children.length > 0) return 'folder-multiple-outline';
    return 'folder-outline';
  })();

  // unseen mails
  const unseenMails: UnseenMailsCount = (() => {
    if (item.specialUse === EFolderSystemSpecialUse.TRASH) return '' as const;
    if (item.specialUse === EFolderSystemSpecialUse.DRAFTS) return item.total;

    return item.unseen > 0 ? item.unseen : '';
  })();

  // router params
  const toParams: RouteLocationRaw = {
    name: ROUTE_NAMES.MAILBOX,
    params: {
      userIndex: route.params.userIndex,
      mailboxId: item.id
    }
  };

  // is active
  const isActive = route.params.mailboxId === item.id;
  // is opened
  const isOpened = openedFolders.value[item.id] ?? false;

  // toggle icon
  const toggleIcon = isOpened ? 'mdi-chevron-up' : 'mdi-chevron-down';

  return { systemFolderIcon, unseenMails, toParams, isActive, isOpened, toggleIcon };
};

const itemState = computed<Record<IHashedFolderItem['id'], FolderListItemComputedState>>(() => {
  const map: Record<IHashedFolderItem['id'], FolderListItemComputedState> = {};

  for (const item of props.items) {
    map[item.id] = computeForItem(item);
  }
  return map;
});

const handleDragEnter = (item: IHashedFolderItem) => {
  setDraggedOverFolder(item.id);
};

const handleDragLeave = (e: DragEvent, item: IHashedFolderItem) => {
  // Ignore leaves when moving within the same item (into its children)
  const current = e.currentTarget as Node | null;
  const related = (e.relatedTarget as Node | null) ?? null;
  if (current && related && current.contains(related)) {
    return;
  }
  // Only clear if leaving the currently highlighted item
  if (draggedOverFolder.value === item.id) {
    setDraggedOverFolder(null);
  }
};

const handleElementDrop = (item: IHashedFolderItem) => {
  // Clear highlight on drop
  setDraggedOverFolder(null);
  handleDrop(item);
};
</script>

<template>
  <template v-for="item in props.items" :key="item.hash">
    <RouterLink
      :to="itemState[item.id]?.toParams"
      :class="[
        `pl-${Math.min(3, props.count) * 4}`,
        {
          'folder-list-item--disabled': item.hidden,
          'folder-list-item--dragged-over': draggedOverFolder === item.id,
          'folder-list-item--active': itemState[item.id]?.isActive
        }
      ]"
      :data-test-id="`folder-list-item-${item.id}`"
      class="folder-list-item"
      @dragenter="() => handleDragEnter(item)"
      @dragleave="handleDragLeave($event, item)"
      @drop="() => handleElementDrop(item)"
      @dragover.prevent="() => false"
    >
      <VIcon :icon="`mdi-${itemState[item.id]?.systemFolderIcon}`"></VIcon>

      <div class="folder-list-item__title" :title="item.name">
        {{ item.name }}
      </div>

      <div class="folder-list-item__append">
        <span v-if="itemState[item.id]?.unseenMails" class="text-title-sm">
          {{ itemState[item.id]?.unseenMails }}
        </span>

        <text-btn
          v-if="item.children.length"
          :data-test-id="`folder-list-item-children-${item.id}`"
          class="py-0"
          color="on-primary"
          size="24"
          @click.prevent.stop="() => toggleFolder(item.id)"
        >
          <VIcon :icon="itemState[item.id]?.toggleIcon" />
        </text-btn>
      </div>
    </RouterLink>

    <FolderListGroup
      v-if="item.children && item.children.length && itemState[item.id]?.isOpened"
      :key="item.id"
      :count="props.count + 1"
      :items="item.children"
    />
  </template>
</template>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.folder-list {
  &-item {
    align-items: center;
    display: grid;
    flex: none;
    grid-template-areas: 'prepend content append';
    grid-template-columns: max-content 1fr auto;
    outline: none;
    max-width: 100%;
    padding-right: var(--zwm-folder-list-item-padding-x);
    padding-top: var(--zwm-folder-list-item-padding-y);
    padding-bottom: var(--zwm-folder-list-item-padding-y);
    position: relative;
    text-decoration: none;
    color: rgb(var(--v-theme-alert-close));
    gap: 0.5rem;

    &:hover,
    &:focus,
    &:focus-visible,
    &:focus-within,
    &:active,
    &--dragged-over {
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgb(var(--v-theme-alert-close), var(--v-focus-opacity));
        z-index: 1;
        pointer-events: none;
      }
    }

    &__append {
      display: flex;
      gap: 0.5rem;
      flex-flow: row nowrap;
      align-items: center;
    }

    &--disabled {
      cursor: initial;
      pointer-events: none;
      color: rgb(var(--v-theme-outline)) !important;

      .folder-list-item__append {
        pointer-events: all;
        color: rgb(var(--v-theme-alert-close));
      }
    }

    &--active {
      background-color: rgb(var(--v-theme-secondary-container));
      color: rgb(var(--v-theme-on-secondary-container));
      border-bottom-right-radius: 0.375rem;
      border-top-right-radius: 0.375rem;
    }

    &__title {
      font-size: var(--zwm-folder-list-item-font-size);
      line-height: 1.25rem;
      font-weight: 500;
      letter-spacing: normal;
      text-transform: none;
      hyphens: auto;
      overflow: hidden;
      padding: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      word-break: normal;
      overflow-wrap: break-word;
    }

    .v-icon {
      opacity: unset;
    }
  }
}
</style>
